import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import {
  useReprocessDocument,
  useRequestDocumentUploadUrl,
} from "@elation/api-client/src/stitch-web";
import type { UploadUrlRequestMimeType } from "@elation/api-client/src/stitch-web/models";

import { events } from "@src/shared/utils/observability/events";

import type { GapListUploadV2 } from "../GapListUploadRow";

interface UseGapListUploadsOptions {
  patientId?: number;
  encounterId?: number;
}

export type AgendaGapListUploadsControl = ReturnType<typeof useGapListUploads>;

export function useGapListUploads({
  patientId,
  encounterId,
}: UseGapListUploadsOptions) {
  const [uploads, setUploads] = useState<GapListUploadV2[]>([]);
  const uploadGenerationRef = useRef(0);

  const { mutateAsync: requestUploadUrl } = useRequestDocumentUploadUrl();
  const { mutate: reprocess } = useReprocessDocument();

  const { mutateAsync: uploadToS3 } = useMutation({
    mutationFn: async ({ url, file }: { url: string; file: File }) => {
      const response = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`);
      }
    },
  });

  const isCurrentGeneration = useCallback((generation: number) => {
    return generation === uploadGenerationRef.current;
  }, []);

  const removeUpload = useCallback((uploadId: string) => {
    setUploads((current) => current.filter((upload) => upload.id !== uploadId));
  }, []);

  const markUploadFailed = useCallback(
    (uploadId: string, generation: number) => {
      if (!isCurrentGeneration(generation)) return;

      setUploads((current) =>
        current.map((upload) =>
          upload.id === uploadId
            ? {
                ...upload,
                failed: true,
                statusText: "Processing failed",
              }
            : upload,
        ),
      );
    },
    [isCurrentGeneration],
  );

  const uploadFiles = useCallback(
    (files: File[]) => {
      if (patientId == null) return;

      const uploadPatientId = patientId;
      const uploadEncounterId = encounterId;
      const generation = uploadGenerationRef.current;

      void Promise.all(
        files.map(async (file) => {
          const uploadId = crypto.randomUUID();
          let jobId: string | undefined;

          if (!isCurrentGeneration(generation)) return;

          setUploads((current) => [
            {
              id: uploadId,
              fileName: file.name,
              patientId: uploadPatientId,
              encounterId: uploadEncounterId,
              statusText: "Uploading...",
            },
            ...current,
          ]);

          try {
            const { data } = await requestUploadUrl({
              data: {
                fileName: file.name,
                mimeType: file.type as UploadUrlRequestMimeType,
                patientId: uploadPatientId,
              },
            });
            jobId = data.jobId;

            if (!isCurrentGeneration(generation)) return;

            setUploads((current) =>
              current.map((upload) =>
                upload.id === uploadId
                  ? {
                      ...upload,
                      jobId: data.jobId,
                      statusText: "Extracting care gaps...",
                    }
                  : upload,
              ),
            );

            await uploadToS3({ url: data.uploadUrl, file });

            if (!isCurrentGeneration(generation)) return;

            events.documentUploaded.track({
              patient_id: uploadPatientId,
              success: true,
            });
            reprocess({ id: data.documentId });
          } catch (err) {
            if (!isCurrentGeneration(generation)) return;

            console.error("[useGapListUploads] Upload failed", {
              fileName: file.name,
              err,
            });
            events.documentUploaded.track({
              patient_id: uploadPatientId,
              success: false,
              error: "upload_failed",
            });
            if (jobId) {
              markUploadFailed(uploadId, generation);
            } else {
              removeUpload(uploadId);
            }
          }
        }),
      );
    },
    [
      patientId,
      encounterId,
      requestUploadUrl,
      uploadToS3,
      reprocess,
      markUploadFailed,
      removeUpload,
      isCurrentGeneration,
    ],
  );

  const resetUploads = useCallback(() => {
    uploadGenerationRef.current += 1;
    setUploads([]);
  }, []);

  const isUploading = uploads.some(
    (upload) => !upload.failed && upload.jobId == null,
  );

  return {
    uploads,
    uploadFiles,
    removeUpload,
    markUploadFailed,
    resetUploads,
    isUploading,
  };
}
